import type { MatchConfirmedArtifactData } from '@eriko/shared-types';
import { formatCurrency, formatDate } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Check, X } from 'lucide-react';

interface Props {
  data: MatchConfirmedArtifactData;
}

/**
 * Compact post-action card shown after a reconciliation match decision lands.
 * SME version: friendly copy, no jargon — the SME may see this after a
 * reviewer (or the agent on their behalf) attached a receipt that resolved
 * a flagged transaction.
 */
export function MatchConfirmedCard({ data }: Props) {
  const confirmed = data.status === 'CONFIRMED';
  return (
    <div className="mt-3 rounded-lg border border-surface-200 bg-surface-0 text-sm overflow-hidden">
      <header
        className={`px-4 py-2 flex items-center gap-2 ${
          confirmed ? 'bg-emerald-50 border-b border-emerald-100' : 'bg-rose-50 border-b border-rose-100'
        }`}
      >
        {confirmed ? (
          <Check className="w-4 h-4 text-emerald-700" />
        ) : (
          <X className="w-4 h-4 text-rose-700" />
        )}
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            confirmed ? 'text-emerald-700' : 'text-rose-700'
          }`}
        >
          {confirmed ? 'Eriko paired this' : 'Match removed'}
        </span>
        {data.writeback && (
          <Badge
            variant="outline"
            className={`ml-auto text-[10px] ${
              data.writeback.kind === 'success'
                ? 'border-emerald-300 text-emerald-700'
                : 'border-amber-300 text-amber-700'
            }`}
          >
            Books: {data.writeback.kind === 'success' ? 'updated' : 'pending'}
          </Badge>
        )}
      </header>

      <div className="px-4 py-3 space-y-1.5">
        <div className="text-sm text-surface-900 truncate">{data.bankTransaction.description}</div>
        <div className="text-[11px] text-surface-500 font-mono">
          {formatDate(data.bankTransaction.date)} ·{' '}
          <span
            className={data.bankTransaction.type === 'CREDIT' ? 'text-emerald-700' : 'text-surface-700'}
          >
            {data.bankTransaction.type === 'CREDIT' ? '+' : '-'}
            {formatCurrency(Number(data.bankTransaction.amount), 2, data.bankTransaction.currency)}
          </span>
        </div>
        <div className="text-xs text-surface-600">
          paired with{' '}
          <span className="font-medium text-surface-800">{data.matchedObjectSummary}</span>
        </div>
      </div>
    </div>
  );
}
